import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Metric } from "@/components/Metric";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { relativeTime } from "@/lib/format";
import { ChartCard, BarSeries, LineSeries, PieSeries, EmptyChart } from "@/components/Charts";

export default function Overview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get("/company/dashboard").then((r) => setData(r.data));
    api.get("/company/analytics").then((r) => setAnalytics(r.data));
  }, []);

  if (!data) return <div className="overline text-stone-500">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="company-overview">
      <PageHeader
        eyebrow={user?.company?.name || "Company"}
        title={`Welcome back, ${user?.name?.split(" ")[0] || ""}.`}
        description="Tenant workspace for projects, customers, updates, and payments."
      />

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Metric label="Projects" value={data.projects} accent testId="metric-projects" />
        <Metric label="Customers" value={data.customers} testId="metric-customers" />
        <Metric label="Units" value={data.units} sub={`${data.sold_units} sold`} testId="metric-units" />
        <Metric label="Open Tickets" value={data.open_tickets} testId="metric-tickets" />
        <Metric label="Overdue Payments" value={data.overdue_payments} testId="metric-overdue" />
        <Metric label="Recent Updates" value={data.recent_updates?.length || 0} testId="metric-updates" />
      </div>

      {analytics && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="company-charts-row-1">
            <ChartCard title="Collections by Month" subtitle="Customer payments received" accent testId="chart-collections">
              {analytics.collections_by_month?.length > 0 ? (
                <BarSeries data={analytics.collections_by_month} yKey="amount" yLabel="Collected" />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
            <ChartCard title="Update Cadence" subtitle="Construction updates per month" testId="chart-updates-cadence">
              {analytics.updates_by_month?.length > 0 ? (
                <LineSeries data={analytics.updates_by_month} yKey="count" yLabel="Updates" />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
            <ChartCard title="Payment Schedule Status" subtitle="Pending vs paid vs overdue" testId="chart-schedule-status">
              {analytics.schedule_status?.length > 0 ? (
                <PieSeries data={analytics.schedule_status} nameKey="status" valueKey="count" />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="company-charts-row-2">
            <ChartCard title="Projects by Status" subtitle="Portfolio distribution" testId="chart-projects-status">
              {analytics.projects_by_status?.length > 0 ? (
                <PieSeries data={analytics.projects_by_status} nameKey="status" valueKey="count" />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
            <ChartCard title="Tickets by Status" subtitle="Support workload" testId="chart-tickets-status">
              {analytics.tickets_by_status?.length > 0 ? (
                <PieSeries data={analytics.tickets_by_status} nameKey="status" valueKey="count" />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
          </div>
        </>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-stone-500">Latest construction updates</div>
          <Link to="/company/updates" className="text-xs font-bold uppercase tracking-wider text-brand-600 hover:text-brand-700" data-testid="link-all-updates">
            View all →
          </Link>
        </div>
        <div className="timeline">
          {data.recent_updates && data.recent_updates.length > 0 ? (
            data.recent_updates.map((u) => (
              <div key={u.id} className="relative pb-6" data-testid={`update-${u.id}`}>
                <div className="timeline-dot" />
                <div className="grid-surface p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="overline text-brand-600">
                        {u.project?.name} {u.milestone && `· ${u.milestone}`}
                      </div>
                      <h3 className="font-display text-lg font-bold mt-1">{u.title}</h3>
                    </div>
                    <div className="text-xs text-stone-500 font-mono whitespace-nowrap">
                      {relativeTime(u.created_at)}
                    </div>
                  </div>
                  <p className="text-sm text-stone-700 mt-2">{u.body}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="grid-surface border-dashed p-8 text-center text-stone-400 overline">
              No updates published yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
